import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';

// Defining the exact shape of the payload returned by `GET /api/homepage`
export interface HomepageData {
    hero: {
        banners: {
            id: string;
            image: string;
            blurhash: string | null;
            title: string;
            subtitle: string | null;
            tagline: string;
            description: string;
            ctaText: string;
            ctaUrl: string | null;
            secondaryCtaText: string | null;
            secondaryCtaUrl: string | null;
            textAlignment: 'left' | 'center' | 'right';
            theme: 'light' | 'dark';
            badge: string | null;
        }[];
        stats: {
            id: string;
            label: string;
        }[];
    };
    metadata: {
        id: string;
        title: string;
        description: string;
        ogImage: string | null;
        announcementEnabled: boolean;
        announcementText: string | null;
        announcementLink: string | null;
        aiChatbotEnabled: boolean;
        aiChatbotWebhook: string | null;
        navLinks?: {
            id: string;
            label: string;
            url: string;
            openInNewTab: boolean;
            isActive: boolean;
            sortOrder: number;
        }[];
    };
    categories: {
        id: string;
        slug: string;
        image: string | null;
        name: string;
    }[];
    brands?: {
        id: string;
        slug: string;
        image: string | null;
        name: string;
        nameAr?: string;
    }[];
    sections: {
        id: string;
        sectionType: string;
        heading: string;
        desc: string | null;
        ctaText: string | null;
        ctaUrl: string | null;
        image: string | null;
        blurhash: string | null;
        isActive: boolean;
        sortOrder: number;
        productIds: string[];
        resolvedProducts?: any[];
    }[];
    services: {
        id: string;
        cardKey: 'branch_locator' | 'prescription' | 'home_tryon';
        title: string;
        desc: string;
        ctaText: string | null;
        ctaUrl: string | null;
        image: string | null;
        blurhash: string | null;
        icon: string | null;
    }[];
    craftsmanship: {
        id: string;
        title: string;
        description: string;
        position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    }[];
    socialProof: {
        id: string;
        image: string;
        blurhash: string | null;
        handle: string;
    }[];
    footer: {
        description: string;
        copyright: string;
        newsletterHeading: string;
        newsletterPromo: string;
        whatsappEnabled: boolean;
        whatsappNumber: string | null;
    } | null;
}

/**
 * Fetches the entire public homepage payload in a single, high-performance request.
 * The backend handles localization projection based on the 'Accept-Language' header.
 */
export const useHomepage = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language || 'en';

    return useQuery({
        queryKey: ['homepage', currentLang],
        queryFn: async (): Promise<HomepageData> => {
            const response = await api.get('/homepage', {
                headers: {
                    // Force the API to project bilingual data down to the requested locale
                    'Accept-Language': currentLang,
                }
            });
            return response.data.data;
        },
        // Cache the homepage data aggressively on the client
        staleTime: 5 * 60 * 1000, // 5 minutes fresh
        gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
    });
};
